<template>
  <div>
    <template v-if="totalShown > 0">
    <section
      v-for="stage in stagesInOrder"
      :key="stage.value"
      :class="{ 'prospect-group-empty': familiesByStage[stage.value].length === 0 }"
    >
      <header
        class="prospect-group-header"
        :class="{ 'prospect-group-header-clickable': familiesByStage[stage.value].length > 0 }"
        @click="onHeaderClick(stage)"
      >
        <span class="prospect-group-dot" :class="'is-stage-' + stage.value"></span>
        <h4 class="prospect-group-title">{{ stage.label }}</h4>
        <span class="prospect-group-count">{{ familiesByStage[stage.value].length }}</span>
        <i
          v-if="familiesByStage[stage.value].length > 0"
          class="fa-solid fa-chevron-down prospect-group-chevron"
          :class="{ 'is-collapsed': collapsed[stage.value] }"
          aria-hidden="true"
        ></i>
      </header>
      <p
        v-if="stage.subtext && familiesByStage[stage.value].length > 0"
        class="prospect-group-subtext"
      >
        {{ stage.subtext }}
      </p>
      <div
        v-if="familiesByStage[stage.value].length > 0"
        v-show="!collapsed[stage.value]"
        class="grid grid-cols-1 gap-2"
      >
        <prospect-row
          v-for="family in familiesByStage[stage.value]"
          :key="stage.value + '-' + family.id"
          :family="family"
          :families-api-url="familiesApiUrl"
          :team-name="teamName"
          :current-author-id="currentAuthorId"
          :current-author-type="currentAuthorType"
          mode="all"
          @open="$emit('open', $event)"
          @note-created="$emit('note-created', $event)"
          @stage-change="$emit('stage-change', $event)"
        />
      </div>
    </section>
    </template>

    <!-- Empty state when nothing matches at all -->
    <div
      v-if="!loading && totalShown === 0"
      class="text-center p-y-lg"
    >
      <p class="text-16 text-gray-700 m-b-xs">No leads match your filters.</p>
      <p class="text-14 text-gray-500">Try clearing the search or stage filter.</p>
    </div>
  </div>
</template>

<script>
import ProspectRow from './prospect_row.vue';

const STAGES = [
  {
    value: 'inquiry',
    label: 'Interested',
    subtext: "New inquiries who haven't tried a class yet.",
  },
  {
    value: 'trial_scheduled',
    label: 'Trial Scheduled',
    subtext: 'Signed up for a trial — waiting for them to attend.',
  },
  {
    value: 'trialing',
    label: 'Trialing',
    subtext: 'Currently using their trial passes.',
  },
  {
    value: 'trial_complete',
    label: 'Trial Complete',
    subtext: 'Finished their trial — awaiting their decision.',
  },
  {
    value: 'converted',
    label: 'Converted',
    subtext: 'Joined the team via a camp, full-season, or subscription registration.',
  },
  {
    value: 'didnt_join',
    label: "Didn't Join",
    subtext: 'Coach marked these as not joining; lost reason captured on the card.',
  },
  {
    value: 'archived',
    label: 'Archived',
    subtext: 'Removed from the active pipeline.',
  },
];

const TERMINAL_STAGES = new Set(['converted', 'didnt_join', 'archived']);

function defaultCollapsedFor(stage) {
  const stored = localStorage.getItem(`prospect_all_list_collapsed_${stage}`);
  if (stored === null) return TERMINAL_STAGES.has(stage); // terminals collapsed by default
  return stored === 'true';
}

export default {
  name: 'prospect-all-list',
  components: { ProspectRow },
  props: {
    families: { type: Array, default: () => [] },
    familiesApiUrl: { type: String, required: true },
    teamName: { type: String, default: '' },
    currentAuthorId: { type: Number, default: null },
    currentAuthorType: { type: String, default: '' },
    activeStage: { type: String, default: '' },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      collapsed: STAGES.reduce((acc, s) => {
        acc[s.value] = defaultCollapsedFor(s.value);
        return acc;
      }, {}),
    };
  },
  computed: {
    stagesInOrder() {
      // When a stage filter is active, only render that stage's section.
      if (this.activeStage) return STAGES.filter((s) => s.value === this.activeStage);
      return STAGES;
    },
    familiesByStage() {
      const groups = STAGES.reduce((acc, s) => { acc[s.value] = []; return acc; }, {});
      this.families.forEach((family) => {
        const primary = this.primaryProspect(family);
        if (!primary) return;
        const { stage } = primary;
        if (groups[stage]) groups[stage].push(family);
      });
      return groups;
    },
    totalShown() {
      return this.stagesInOrder.reduce((sum, s) => sum + this.familiesByStage[s.value].length, 0);
    },
  },
  watch: {
    // When user picks a stage from the toolbar, force-expand that section so
    // they actually see the rows they filtered to.
    activeStage(stage) {
      if (stage && this.collapsed[stage]) {
        this.toggleCollapse(stage);
      }
    },
  },
  methods: {
    primaryProspect(family) {
      if (!family.prospects || family.prospects.length === 0) return null;
      return [...family.prospects].sort((a, b) => {
        const aD = a.stage_changed_at ? new Date(a.stage_changed_at).getTime() : 0;
        const bD = b.stage_changed_at ? new Date(b.stage_changed_at).getTime() : 0;
        return bD - aD;
      })[0];
    },
    toggleCollapse(stage) {
      this.$set(this.collapsed, stage, !this.collapsed[stage]);
      localStorage.setItem(`prospect_all_list_collapsed_${stage}`, this.collapsed[stage]);
    },
    // Empty stages render their header but have no body to expand, so the
    // header click is a no-op for them.
    onHeaderClick(stage) {
      if (this.familiesByStage[stage.value].length === 0) return;
      this.toggleCollapse(stage.value);
    },
  },
};
</script>
